/** @jsx jsx */
import { css, jsx } from '@emotion/core'
import {
  Alert,
  Heading,
  HFlow,
  isEmpty,
  Table,
  TableBody,
  TableHead,
  TableHeader,
  TableRow,
  Text,
  VFlow,
} from 'bold-ui'
import { HLabel } from 'components/HLabel'
import { TableBox } from 'components/table'
import { useHistoricoVisitaDomiciliarQuery } from 'graphql/hooks.generated'
import { memo } from 'react'
import { stringGrouper } from 'util/string/string-grouper'

interface HistoricoVisitaDomiciliarPanelProps {
  cpfOuCnsCidadao: string
  uuidAtend: ID
}

function HistoricoVisitaDomiciliarPanel({ uuidAtend, cpfOuCnsCidadao }: HistoricoVisitaDomiciliarPanelProps) {
  const {
    data: { historicoVisitaDomiciliar: detalheVisitaDomiciliar },
  } = useHistoricoVisitaDomiciliarQuery({
    variables: {
      input: {
        uuidAtend,
        cpfOuCnsCidadao,
        isOrigemPec: false,
      },
    },
  })

  return (
    !!detalheVisitaDomiciliar && (
      <VFlow>
        {detalheVisitaDomiciliar.stVisitaCompartilhada && (
          <Alert type='info' inline>
            <Text color='primary' fontWeight='bold'>
              Visita compartilhada com outro profissional
            </Text>
          </Alert>
        )}
        <TableBox>
          <Table>
            <TableHead>
              <TableRow>
                <TableHeader>Motivo da visita</TableHeader>
              </TableRow>
            </TableHead>
            <TableBody>
              <div
                css={css`
                  padding: 0.5rem 1rem;
                `}
              >
                <TableRow>
                  <Text>
                    {detalheVisitaDomiciliar.motivoVisita && stringGrouper(...detalheVisitaDomiciliar.motivoVisita)}
                  </Text>
                </TableRow>
                {!isEmpty(detalheVisitaDomiciliar.buscaAtiva) && (
                  <TableRow>
                    <VFlow
                      vSpacing={0.5}
                      style={css`
                        padding-top: 0.5rem;
                      `}
                    >
                      <Text fontWeight='bold'>Busca ativa</Text>
                      <Text>
                        <Text>{stringGrouper(...detalheVisitaDomiciliar.buscaAtiva)}</Text>
                      </Text>
                    </VFlow>
                  </TableRow>
                )}
                {!isEmpty(detalheVisitaDomiciliar.acompanhamento) && (
                  <TableRow>
                    <VFlow
                      vSpacing={0.5}
                      style={css`
                        padding-top: 0.5rem;
                      `}
                    >
                      <Text fontWeight='bold'>Acompanhamento</Text>
                      <Text>{stringGrouper(...detalheVisitaDomiciliar.acompanhamento)}</Text>
                    </VFlow>
                  </TableRow>
                )}
                {!isEmpty(detalheVisitaDomiciliar.controleAmbiental) && (
                  <TableRow>
                    <VFlow
                      vSpacing={0.5}
                      style={css`
                        padding-top: 0.5rem;
                      `}
                    >
                      <Text fontWeight='bold'>Controle ambiental/vetorial</Text>
                      <Text>{stringGrouper(...detalheVisitaDomiciliar.controleAmbiental)}</Text>
                    </VFlow>
                  </TableRow>
                )}
              </div>
            </TableBody>
          </Table>
        </TableBox>
        <TableBox>
          <Table>
            <TableHead>
              <TableRow>
                <TableHeader>Antropometria, sinais vitais e glicemia capilar</TableHeader>
              </TableRow>
            </TableHead>
            <TableBody>
              <div
                css={css`
                  padding: 0.5rem 1rem;
                `}
              >
                {detalheVisitaDomiciliar.peso ||
                detalheVisitaDomiciliar.altura ||
                detalheVisitaDomiciliar.temperatura ||
                detalheVisitaDomiciliar.medicaoGlicemia ||
                detalheVisitaDomiciliar.medicaoPressaoArterial ||
                detalheVisitaDomiciliar.dimensaoTipoGlicemia ? (
                  <TableRow>
                    <HFlow>
                      <VFlow vSpacing={0.5}>
                        {detalheVisitaDomiciliar.peso && (
                          <HLabel title='Peso:'>{`${detalheVisitaDomiciliar.peso} kg`}</HLabel>
                        )}
                        {detalheVisitaDomiciliar.altura && (
                          <HLabel title='Altura:'>{`${detalheVisitaDomiciliar.altura} cm`}</HLabel>
                        )}
                      </VFlow>
                      <VFlow vSpacing={0.5}>
                        {detalheVisitaDomiciliar.temperatura && (
                          <HLabel title='Temperatura:'>{`${detalheVisitaDomiciliar.temperatura} ºC`}</HLabel>
                        )}
                        {detalheVisitaDomiciliar.medicaoPressaoArterial && (
                          <HLabel title='Pressão arterial:'>{`${detalheVisitaDomiciliar.medicaoPressaoArterial} mmHg`}</HLabel>
                        )}
                      </VFlow>
                      {detalheVisitaDomiciliar.medicaoGlicemia && (
                        <VFlow vSpacing={0}>
                          <HLabel title='Glicemia capilar:'>{`${detalheVisitaDomiciliar.medicaoGlicemia} mg/dL`}</HLabel>
                          <Text>{`(Momento da coleta: ${detalheVisitaDomiciliar.dimensaoTipoGlicemia.descricao})`}</Text>
                        </VFlow>
                      )}
                    </HFlow>
                  </TableRow>
                ) : (
                  <Text fontStyle='italic'>Não registrado nesse atendimento</Text>
                )}
              </div>
            </TableBody>
          </Table>
        </TableBox>
        <VFlow vSpacing={0.5}>
          <Heading level={4}>Desfecho</Heading>
          <Text>{detalheVisitaDomiciliar.dimensaoDesfechoVisita.descricao}</Text>
        </VFlow>
      </VFlow>
    )
  )
}

export default memo(HistoricoVisitaDomiciliarPanel)
